// Entry point of dist/bubble.js. Embed with:
//   <script src="https://support.example.org/bubble.js" async></script>
// Optional data attributes: data-tool, data-position, data-offset-x, data-offset-y,
// data-launcher, data-z-index, data-auto-avoid, data-hidden, data-exclude, data-api, data-privacy-url.
import { DEFAULT_PRIVACY, DEFAULT_SCREENSHOT, DEFAULT_WIDGET } from '../shared/defaults.ts';
import type { Corner, Launcher, PublicConfig, ReportType, WidgetSettings } from '../shared/types.ts';
import { installErrorBuffer } from './context.ts';
import { SupportBubble } from './ui.ts';

declare const __GSB_VERSION__: string;
const VERSION = typeof __GSB_VERSION__ === 'string' ? __GSB_VERSION__ : 'dev';

export interface GreenSupportBubbleApi {
  version: string;
  open(type?: ReportType): void;
  close(): void;
  toggle(): void;
  /** Pre-fills the contact e-mail (e.g. from the logged-in user of the host application). */
  setUser(user: { name?: string; email?: string }): void;
  /** Adds key/value pairs to every report, e.g. { appVersion: '4.2.1', mandant: 'KV Musterstadt' }. */
  setContext(values: Record<string, unknown>): void;
  show(): void;
  hide(): void;
  destroy(): void;
}

declare global {
  interface Window {
    GreenSupportBubble?: GreenSupportBubbleApi;
  }
}

function fallbackConfig(): PublicConfig {
  return {
    version: 'offline',
    organisation: 'GRÜNE',
    allowedOrigins: [],
    fallbackContactLabel: 'Zentrales Support-Team',
    widget: DEFAULT_WIDGET,
    screenshot: DEFAULT_SCREENSHOT,
    privacy: DEFAULT_PRIVACY,
    tools: [],
  };
}

async function loadConfig(apiBase: string): Promise<PublicConfig> {
  try {
    // Static and cacheable: no cookies, no page URL (not even as Referer) – loading the bubble
    // does not track anyone. The browser's Origin header tells the server which website uses
    // the bubble (admin overview, website only).
    const response = await fetch(`${apiBase}/api/v1/config?v=${encodeURIComponent(VERSION)}`, { credentials: 'omit', mode: 'cors', referrerPolicy: 'no-referrer' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as PublicConfig;
  } catch (err) {
    console.warn('[Green Support Bubble] Konfiguration nicht erreichbar, nutze Standardwerte:', err);
    return fallbackConfig();
  }
}

function numberAttr(value: string | undefined): number | undefined {
  if (value === undefined || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function boolAttr(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  return value === '' || value === 'true' || value === '1';
}

function readOverrides(data: DOMStringMap): Partial<WidgetSettings> {
  const overrides: Partial<WidgetSettings> = {};
  if (data.position && ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(data.position)) overrides.position = data.position as Corner;
  if (data.launcher === 'bubble' || data.launcher === 'tab') overrides.launcher = data.launcher as Launcher;
  const offsetX = numberAttr(data.offsetX);
  const offsetY = numberAttr(data.offsetY);
  const zIndex = numberAttr(data.zIndex);
  const autoAvoid = boolAttr(data.autoAvoid);
  const hidden = boolAttr(data.hidden);
  if (offsetX !== undefined) overrides.offsetX = offsetX;
  if (offsetY !== undefined) overrides.offsetY = offsetY;
  if (zIndex !== undefined) overrides.zIndex = zIndex;
  if (autoAvoid !== undefined) overrides.autoAvoid = autoAvoid;
  if (hidden !== undefined) overrides.hidden = hidden;
  return overrides;
}

(function bootstrap() {
  // Included twice (e.g. via proxy injection and by the app itself)? Keep the first one.
  if (window.GreenSupportBubble) return;

  const script = document.currentScript as HTMLScriptElement | null;
  const data = script?.dataset ?? {};
  const scriptUrl = script?.src ? new URL(script.src, location.href) : new URL(location.href);
  const apiBase = (data.api ?? new URL('.', scriptUrl).toString()).replace(/\/$/, '');
  const nonce = script?.nonce || undefined;

  const stopErrorBuffer = installErrorBuffer();
  let widget: SupportBubble | undefined;
  const queue: Array<(w: SupportBubble) => void> = [];
  const call = (fn: (w: SupportBubble) => void) => (widget ? fn(widget) : queue.push(fn));

  const api: GreenSupportBubbleApi = {
    version: VERSION,
    open: (type) => call((w) => w.open(type)),
    close: () => call((w) => w.close()),
    toggle: () => call((w) => w.toggle()),
    setUser: (user) => call((w) => w.setUser(user)),
    setContext: (values) => call((w) => w.setContext(values)),
    show: () => call((w) => w.showLauncher(true)),
    hide: () => call((w) => w.showLauncher(false)),
    destroy: () => {
      widget?.destroy();
      widget = undefined;
      stopErrorBuffer();
      delete window.GreenSupportBubble;
    },
  };
  window.GreenSupportBubble = api;

  async function start(): Promise<void> {
    const config = await loadConfig(apiBase);
    if (window.GreenSupportBubble !== api) return; // destroyed while loading
    widget = new SupportBubble({
      config,
      apiBase,
      rendererUrl: `${apiBase}/screenshot.js?v=${encodeURIComponent(VERSION)}`,
      nonce,
      forcedToolId: data.tool,
      overrides: readOverrides(data),
      excludePaths: data.exclude ? data.exclude.split(',').map((p) => p.trim()).filter(Boolean) : undefined,
      privacyUrl: data.privacyUrl ?? config.privacyUrl,
      version: VERSION,
    });
    widget.mount();
    queue.splice(0).forEach((fn) => fn(widget as SupportBubble));
    window.dispatchEvent(new CustomEvent('green-support-bubble:ready', { detail: api }));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => void start(), { once: true });
  else void start();
})();
